import { useState, useRef } from "react";
import { QrCode, Download, Copy, Link, Calendar, Sparkles, Home } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import QRCodeSVG from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminQRCodes = ({ user }) => {
  const [customUrl, setCustomUrl] = useState("");
  const [qrCodes, setQrCodes] = useState({});
  const canvasRefs = useRef({});

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const predefinedPages = [
    { id: "home", label: "Homepage", url: baseUrl, icon: Home },
    { id: "booking", label: "Booking Page", url: `${baseUrl}/booking`, icon: Calendar },
    { id: "promo", label: "Promo Boost", url: `${baseUrl}/promo`, icon: Sparkles },
  ];

  const generateQR = async (id, url) => {
    try {
      const dataUrl = await QRCodeSVG.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#D4AF37',
          light: '#0A0A0B'
        }
      });
      setQrCodes(prev => ({ ...prev, [id]: dataUrl }));
    } catch {
      toast.error("Failed to generate QR code");
    }
  };

  const downloadPNG = (id) => {
    const dataUrl = qrCodes[id];
    if (!dataUrl) return;
    
    const link = document.createElement('a');
    link.download = `dopeinhd-qr-${id}.png`;
    link.href = dataUrl;
    link.click();
    toast.success("PNG downloaded!");
  };

  const downloadSVG = async (id, url) => {
    try {
      const svg = await QRCodeSVG.toString(url, {
        type: 'svg',
        width: 300,
        margin: 2,
        color: {
          dark: '#D4AF37',
          light: '#0A0A0B'
        }
      });
      
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `dopeinhd-qr-${id}.svg`;
      link.href = downloadUrl;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success("SVG downloaded!");
    } catch (error) {
      toast.error("Failed to download SVG");
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard!");
  };

  const handleCustomGenerate = () => {
    if (!customUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    generateQR("custom", customUrl);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-qr-codes">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#FAFAFA] flex items-center gap-3">
          <QrCode size={28} className="text-[#D4AF37]" />
          QR Code Generator
        </h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Generate QR codes for your pages</p>
      </div>

      {/* Predefined Pages */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {predefinedPages.map((page) => (
          <Card key={page.id} className="bg-[#121214] border-[#27272A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#FAFAFA] flex items-center gap-2">
                <page.icon size={20} className="text-[#D4AF37]" />
                {page.label}
              </CardTitle>
              <p className="text-xs text-[#71717A] truncate">{page.url}</p>
            </CardHeader>
            <CardContent>
              {qrCodes[page.id] ? (
                <div className="space-y-4">
                  <div className="bg-[#0A0A0B] p-4 rounded-lg flex items-center justify-center">
                    <img 
                      src={qrCodes[page.id]} 
                      alt={`QR code for ${page.label}`}
                      className="w-48 h-48"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => downloadPNG(page.id)}
                      size="sm"
                      className="flex-1 btn-gold"
                    >
                      <Download size={14} className="mr-1" /> PNG
                    </Button>
                    <Button 
                      onClick={() => downloadSVG(page.id, page.url)}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-[#27272A] text-[#FAFAFA]"
                    >
                      <Download size={14} className="mr-1" /> SVG
                    </Button>
                  </div>
                  <Button 
                    onClick={() => copyToClipboard(page.url)}
                    size="sm"
                    variant="ghost"
                    className="w-full text-[#A1A1AA]"
                  >
                    <Copy size={14} className="mr-1" /> Copy URL
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => generateQR(page.id, page.url)}
                  className="w-full btn-gold"
                >
                  <QrCode size={18} className="mr-2" />
                  Generate QR Code
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Custom URL */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader>
          <CardTitle className="text-lg text-[#FAFAFA] flex items-center gap-2">
            <Link size={20} className="text-[#D4AF37]" />
            Custom URL
          </CardTitle>
          <p className="text-sm text-[#A1A1AA]">Generate QR code for any URL (e.g., /p/username for marketing pages)</p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-[#A1A1AA]">Enter URL</Label>
              <Input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder={`${baseUrl}/p/artistname`}
                className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <Button onClick={handleCustomGenerate} className="btn-gold">
              Generate
            </Button>
          </div>

          {qrCodes.custom && (
            <div className="mt-6 flex flex-col items-center gap-4">
              <div className="bg-[#0A0A0B] p-4 rounded-lg">
                <img 
                  src={qrCodes.custom} 
                  alt="Custom QR code"
                  className="w-48 h-48"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => downloadPNG("custom")}
                  className="btn-gold"
                >
                  <Download size={16} className="mr-2" /> Download PNG
                </Button>
                <Button 
                  onClick={() => downloadSVG("custom", customUrl)}
                  variant="outline"
                  className="border-[#27272A] text-[#FAFAFA]"
                >
                  <Download size={16} className="mr-2" /> Download SVG
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminQRCodes;
